import { Menu, Flex, Icon, Switch, Td, Text, Tr, useDisclosure } from '@televet/kibble-ui';
import { useTracking } from 'shared/providers/TrackingProvider';
import React from 'react';
import { LoyaltyEvents } from 'shared/providers/TrackingProvider/EventNames';
import { STATUSES, useCustomToast } from 'shared/hooks/useCustomToast';
import {
  LoyaltyProgramRewardFragment,
  LoyaltyRewardAvailability,
  useUpdateLoyaltyProgramRewardMutation,
} from 'shared/types/graphql';
import { RewardAction } from '..';
import { DeleteConfirmationModal } from './DeleteConfirmationModal';

interface RewardRowProps {
  reward: LoyaltyProgramRewardFragment;
  setSelectedReward: React.Dispatch<React.SetStateAction<LoyaltyProgramRewardFragment | undefined>>;
  index: number;
  triggerRefetch: () => void;
}

export const RewardRow = ({ reward, setSelectedReward, index, triggerRefetch }: RewardRowProps): JSX.Element => {
  const { toastMessagePop } = useCustomToast();
  const {
    isOpen: isDeleteRewardModalOpen,
    onOpen: openDeleteRewardModal,
    onClose: closeDeleteRewardModal,
  } = useDisclosure();
  const { trackEvent } = useTracking();

  const [updateReward, { loading: updatingReward }] = useUpdateLoyaltyProgramRewardMutation();

  const handleSwitchChange = async (): Promise<void> => {
    trackEvent(LoyaltyEvents.RewardsToggle, { type: 'individual' });
    try {
      const newAvailability =
        reward.availability === LoyaltyRewardAvailability.Published
          ? LoyaltyRewardAvailability.Unpublished
          : LoyaltyRewardAvailability.Published;

      await updateReward({
        variables: {
          where: {
            id: reward.id,
          },
          data: {
            availability: newAvailability,
          },
        },
      });
      toastMessagePop();
      triggerRefetch();
    } catch {
      toastMessagePop(
        'Whoops! There was a problem updating the rewards, please refresh the page and try again.',
        STATUSES.error,
      );
    }
  };

  return (
    <Tr backgroundColor={index % 2 ? 'background.lessSubtle' : 'transparent'}>
      <Td px={4} py={5} minW="230px">
        <Text whiteSpace="initial" isTruncated>
          {reward.title}
        </Text>
      </Td>
      <Td px={4} py={5}>
        <Flex alignItems="center">
          <Icon name="star" size="sm" variant="warning" />
          <Text ml={1}>{reward.pointCost}</Text>
        </Flex>
      </Td>
      <Td px={4} py={5}>
        <Text whiteSpace="initial" isTruncated>
          {reward.description}
        </Text>
      </Td>
      <Td px={4} py={5} textAlign="end">
        <Switch
          isChecked={reward.availability === LoyaltyRewardAvailability.Published}
          onChange={handleSwitchChange}
          isDisabled={updatingReward}
          formControlStyle={{ width: 'auto', display: 'flex', justifyContent: 'flex-end' }}
        />
      </Td>
      <Td px={4} py={5}>
        <Menu
          buttonProps={{
            rightIconName: 'dotsVertical',
            size: 'xs',
            text: '',
            variant: 'ghostNeutral',
          }}
          listProps={{
            onSelect: (menuItem): void => {
              const isEditing = menuItem.value === RewardAction.Edit;
              trackEvent(isEditing ? LoyaltyEvents.RewardsEditClick : LoyaltyEvents.RewardsDeleteClick);
              if (isEditing) {
                setSelectedReward(reward);
              } else {
                openDeleteRewardModal();
              }
            },
            menuItems: [
              {
                label: RewardAction.Edit,
                value: RewardAction.Edit,
              },
              {
                label: RewardAction.Delete,
                value: RewardAction.Delete,
              },
            ],
            minW: '150px',
          }}
        />
        {isDeleteRewardModalOpen && (
          <DeleteConfirmationModal
            isOpen={isDeleteRewardModalOpen}
            onClose={closeDeleteRewardModal}
            selectedRewardId={reward.id}
            triggerRefetch={triggerRefetch}
          />
        )}
      </Td>
    </Tr>
  );
};
